import * as analytics from 'analytics/Analytics';
import {ActionOrigin, WORKFLOWS_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';

import {
  FlowchartEdge,
  ProcessFlowchartEdgeType,
  ProcessFlowchartNodeType,
} from '../processFlowchartConfigEditorConstants';
import {
  FlowchartNode,
  isInteractiveTaskNode,
  isInteractivePlaceholderNode,
  isPlaceholderForTaskSchemaCreateData,
} from '../processFlowchartConfigEditorNodeTypes';
import {Transition} from '../types/transition/TransitionTypes';

/**
 * Metadata for a node being deleted, used for analytics tracking.
 */
export interface DeletedNodeMetadata {
  nodeName: string;
  nodeType: ProcessFlowchartNodeType;
  executionType: ProcessExecutionType | null;
  taskSchemaId: string | null;
}

/**
 * Builds a combined identifier string from node type and execution type.
 * For task schema nodes: "EntryTaskSchemaNode", "DirectTaskSchemaNode", etc.
 * For non-task nodes: "RouterNode", "StopNode", etc.
 */
export function buildExecutionNodeTypeIdentifier(
  nodeType: ProcessFlowchartNodeType,
  executionType: ProcessExecutionType | null
): string {
  if (executionType != null) {
    const executionTypeTitle = executionType.charAt(0).toUpperCase() + executionType.slice(1).toLowerCase();
    return `${executionTypeTitle}${nodeType}`;
  }
  return nodeType;
}

/**
 * Extracts the execution type from a FlowchartNode's data.
 */
export function getExecutionTypeFromNode(node: FlowchartNode): ProcessExecutionType | null {
  if (isInteractiveTaskNode(node)) {
    return node.data.taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType;
  }
  if (
    isInteractivePlaceholderNode(node) &&
    isPlaceholderForTaskSchemaCreateData(node.data.placeholderForNodeTypeData)
  ) {
    return node.data.placeholderForNodeTypeData.executionType;
  }
  return null;
}

export function getExecutionNodeTypeIdentifierForNode(node: FlowchartNode): string {
  const executionType = getExecutionTypeFromNode(node);
  return buildExecutionNodeTypeIdentifier(node.data.nodeType, executionType);
}

/**
 * Determines if a node's task schema is shared with other nodes remaining on the canvas.
 * Returns true if after removing the deleted nodes, at least one other node with the same
 * task schema will remain on the canvas.
 */
function doesNodeShareTaskSchemaWithOtherNodesOnCanvas(
  nodeTaskSchemaId: string | null,
  allCanvasTaskSchemaIds: Array<string>,
  deletedNodeTaskSchemaIds: Array<string | null>
): boolean {
  if (nodeTaskSchemaId == null) {
    return false;
  }

  const remainingTaskSchemaIds = [...allCanvasTaskSchemaIds];
  for (const deletedId of deletedNodeTaskSchemaIds) {
    if (deletedId != null) {
      const index = remainingTaskSchemaIds.indexOf(deletedId);
      if (index !== -1) {
        remainingTaskSchemaIds.splice(index, 1);
      }
    }
  }

  // Check if the node's task schema exists in remaining nodes
  return remainingTaskSchemaIds.includes(nodeTaskSchemaId);
}

/**
 * Fires analytics events for each deleted node.
 */
export function trackNodeRemovalAnalytics(
  deletedNodesMetadata: Array<DeletedNodeMetadata>,
  taskSchemaIdsOnCanvas: Array<string>,
  flowchartType: string
): void {
  const deletedTaskSchemaIds = deletedNodesMetadata.map((node) => node.taskSchemaId);
  const eventGroupId = analytics.makeAnalyticsEventId();

  deletedNodesMetadata.forEach((node) => {
    const nodeTypeIdentifier = buildExecutionNodeTypeIdentifier(node.nodeType, node.executionType);
    const isDuplicateTaskSchema = doesNodeShareTaskSchemaWithOtherNodesOnCanvas(
      node.taskSchemaId,
      taskSchemaIdsOnCanvas,
      deletedTaskSchemaIds
    );

    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.NODE_REMOVED_FROM_CANVAS, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      eventGroupId,
      nodeType: nodeTypeIdentifier,
      isDuplicateTaskSchema,
      flowchartType,
    });
  });
}

/**
 * Metadata for an edge being deleted, used for analytics tracking.
 */
export interface DeletedEdgeMetadata {
  edgeType: ProcessFlowchartEdgeType;
}

/**
 * Fires analytics events for each deleted edge.
 */
export function trackEdgeRemovalAnalytics(
  edgeTypes: Array<ProcessFlowchartEdgeType>,
  flowchartType: string
): void {
  const edgeTypeCounts = edgeTypes.reduce<Record<string, number>>((counts, edgeType) => {
    counts[edgeType] = (counts[edgeType] ?? 0) + 1;
    return counts;
  }, {});

  analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.DELETED_EDGES_IN_CANVAS, {
    actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
    numEdgesDeleted: edgeTypes.length,
    edgeTypeCounts,
    flowchartType,
  });
}

/**
 * Tracks multi-select analytics when multiple nodes are selected in the canvas.
 * Uses the selection state from React Flow's useOnSelectionChange hook for accurate counts.
 */
export function trackMultiSelectAnalytics(
  selectedNodes: Array<FlowchartNode>,
  selectedEdges: Array<FlowchartEdge>,
  flowchartType: string
): void {
  if (selectedNodes.length > 1) {
    const nodeTypeCounts = selectedNodes.reduce<Record<string, number>>((counts, node) => {
      const nodeTypeIdentifier = getExecutionNodeTypeIdentifierForNode(node);
      counts[nodeTypeIdentifier] = (counts[nodeTypeIdentifier] ?? 0) + 1;
      return counts;
    }, {});

    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.MULTI_SELECTED_IN_CANVAS, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      numNodesSelected: selectedNodes.length,
      numEdgesSelected: selectedEdges.length,
      nodeTypeCounts,
      flowchartType,
    });
  }
}

export function trackOptionalTransitionPromotedAnalytics(
  promotedTransitions: Array<Transition>,
  flowchartType: string
): void {
  for (const transition of promotedTransitions) {
    analytics.trackEvent(WORKFLOWS_ANALYTICS_ACTIONS.OPTIONAL_TRANSITION_PROMOTED, {
      actionOrigin: ActionOrigin.WORKFLOWS_FLOWCHART_FIRST_CONFIGURATION_CANVAS,
      flowchartType,
      transitionId: transition.id,
    });
  }
}
